import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { analyticsApi } from '@/api';
import { AnalyticsData } from '@/types';
import { formatCurrency } from '@/utils/helpers';
import { MetricCard, Card, CardHeader, CardTitle, CardBody } from '@/ui';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Analytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await analyticsApi.getAnalytics();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError('Failed to load analytics data.');
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError('Failed to load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-ink-subtle">Loading analytics...</div>;
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sev-major">{error || 'No analytics data available.'}</div>
      </div>
    );
  }

  // Chart uses brand color at 40% alpha so the bars read as inspector-branded
  // without overwhelming the gridlines. getComputedStyle is the reliable way
  // to resolve `--brand` (defined on :root) into an RGB triplet for rgba().
  const brandRgb =
    typeof window !== 'undefined'
      ? getComputedStyle(document.documentElement).getPropertyValue('--brand').trim() || '47 106 68'
      : '47 106 68';
  const chartData = {
    labels: data.inspectionsByMonth.map((d) => d.month),
    datasets: [
      {
        label: 'Inspections',
        data: data.inspectionsByMonth.map((d) => d.value),
        backgroundColor: `rgb(${brandRgb} / 0.4)`,
        borderColor: `rgb(${brandRgb})`,
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="font-display text-display-sm text-ink mb-8">Analytics</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <MetricCard label="Total Inspections" value={data.totalInspections} tone="brand" />
        <MetricCard label="Total Revenue" value={formatCurrency(data.totalRevenue)} tone="maintenance" />
        <MetricCard label="Average Value" value={formatCurrency(data.avgInspectionValue)} tone="neutral" />
      </div>

      <Card variant="elevated" unpadded>
        <CardHeader>
          <CardTitle>Inspections by Month</CardTitle>
        </CardHeader>
        <CardBody>
          <Bar data={chartData} />
        </CardBody>
      </Card>
    </div>
  );
};

export default Analytics;
